import { FunctionQ } from "@pdfking/structs"
import { Constant, Keys } from "@pdfking/tfunc"
import { useEffect, useState } from "preact/hooks"

import type { DispatchLibrary } from "#public"

const EMPTY_TRANSLATION = new Proxy(
  {},
  {
    get: Constant(Constant("")),
  }
)

type UseTranslation<
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
> = <K extends keyof Names & string>(library: K, language?: Lang) => Names[K]

export const CreateTypedUseTranslation = <
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
>(
  lib: DispatchLibrary<Names, Lang>,
  lang?: NoInfer<Lang> | (() => NoInfer<Lang> | undefined)
): UseTranslation<Names, Lang> => {
  const first_lib = Keys(lib)[0] as string
  const default_lang = Keys(lib[first_lib])[0] as Lang

  const getLanguage = (explicit: Lang | undefined): Lang =>
    explicit || (FunctionQ(lang) ? lang() : lang) || default_lang

  return <K extends keyof Names & string>(library: K, language?: Lang) => {
    const [names, setNames] = useState(EMPTY_TRANSLATION as Names[K])
    const resolved_language = getLanguage(language)

    useEffect(() => {
      void lib[library][resolved_language]().then(setNames)
    }, [library, resolved_language])

    return names
  }
}

// Every entry widened to the same call signature, for callers indexing by a
// dynamic (non-literal) key — a field id, an entity name, an error enum —
// that `DispatchLibrary`'s precise per-key signatures can't express. Same
// runtime as `CreateTypedUseTranslation`, just cast to a looser type; prefer
// the typed version whenever the key is known at the call site.
type LooseTranslations = Record<string, ((ctx?: unknown) => string) | undefined>

export const CreateUseTranslation = CreateTypedUseTranslation as <
  Names extends Record<string, Record<string, (...args: any[]) => string>>,
  Lang extends string,
>(
  lib: DispatchLibrary<Names, Lang>,
  lang?: NoInfer<Lang> | (() => NoInfer<Lang> | undefined)
) => (library: keyof Names & string, language?: Lang) => LooseTranslations
